import { parseDuration, parseTime } from '@utils/time/timeUtils';
import { MAX_CLIP_SECONDS } from '@constants/config';

export function getEpisodeSavedTime(episodePositions, episode) {
  return episode?.url ? episodePositions?.[episode.url] || 0 : 0;
}

export function getPlayingEpisodeUrl(currentEpisode, isPlaying) {
  return isPlaying && currentEpisode?.url ? currentEpisode.url : null;
}

export function serializeAudioState(state) {
  if (!state.currentEpisode) {
    return null;
  }

  return {
    episodeUrl: state.currentEpisode.url,
    episodeTitle: state.currentEpisode.title,
    episodeDate: state.currentEpisode.pubDate,
    episodeDescr: state.currentEpisode.summary,
    currentTime: state.currentTime,
    volume: state.volume,
    playbackRate: state.playbackRate
  };
}

export function deserializeAudioState(savedState) {
  if (!savedState?.episodeUrl) {
    return null;
  }

  return {
    episode: {
      url: savedState.episodeUrl,
      title: savedState.episodeTitle,
      pubDate: savedState.episodeDate,
      summary: savedState.episodeDescr
    },
    savedTime: savedState.currentTime || 0,
    volume: savedState.volume,
    playbackRate: savedState.playbackRate
  };
}

export function shouldSaveEpisodePosition(duration, currentTime) {
  return duration > 0 && currentTime < duration - 1;
}

export function removeEpisodePosition(positions, episodeUrl) {
  if (!episodeUrl || !Object.prototype.hasOwnProperty.call(positions, episodeUrl)) {
    return positions;
  }

  const nextPositions = { ...positions };
  delete nextPositions[episodeUrl];
  return nextPositions;
}

export function validateClipRequest({
  startTime,
  endTime,
  episodeDuration,
  isAudioContextSupported,
  isMobile
}) {
  const start = typeof startTime === 'number' ? startTime : parseTime(startTime);
  const end = typeof endTime === 'number' ? endTime : parseTime(endTime);
  const duration = typeof episodeDuration === 'number'
    ? episodeDuration
    : parseDuration(episodeDuration);

  if (start < 0 || end < 0) {
    return 'Invalid time format. Use MM:SS or HH:MM:SS.';
  }
  if (start >= end) {
    return 'Start time must be before end time.';
  }
  if (end > duration) {
    return 'End time exceeds episode duration.';
  }
  if (end - start > MAX_CLIP_SECONDS) {
    return 'Clips longer than 20 minutes cannot be exported. Please choose a shorter range.';
  }
  if (!isAudioContextSupported) {
    return 'Clip export is not supported in this browser.';
  }
  if (isMobile && end - start > 10 * 60) {
    return 'For reliable mobile exports, please choose a clip of 10 minutes or less.';
  }

  return null;
}

export function estimateClipSize(startTime, endTime) {
  const start = typeof startTime === 'number' ? startTime : parseTime(startTime);
  const end = typeof endTime === 'number' ? endTime : parseTime(endTime);

  if (start < 0 || end <= start) {
    return '0.0';
  }

  return ((end - start) * 16000 / (1024 * 1024)).toFixed(1);
}
